import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useReferralsEnabled } from "@/hooks/useReferralsEnabled";
import { decidePendingReferralRedirect, readPendingReferral } from "@/lib/pendingReferral";

/**
 * Brings a friend back to their invite after the sign-up it triggered.
 *
 * The gap this closes: `/login` round-trips a return path, but ProtectedRoute
 * sends a brand-new user to `/onboarding/0` and that redirect discards it. New
 * users are the entire referral audience, so without this the link works for
 * everyone except the people it is for.
 *
 * Renders nothing. All of the judgement lives in
 * `decidePendingReferralRedirect`, which is pure and tested — this component is
 * only the wiring, because the failure mode here is a redirect loop and a loop is
 * far easier to reason about as a function than as an effect.
 *
 * Fires at most once per mount. The stash is cleared by the invite page on
 * success or on a dead-end refusal, so the ordinary case never comes back; the
 * ref covers the case where a redemption is abandoned mid-flight, which would
 * otherwise re-trigger on every navigation for the life of the stash.
 */
export default function PendingReferralRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, isLoading } = useAuth();
  const { enabled } = useReferralsEnabled();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current || isLoading) return;

    const target = decidePendingReferralRedirect({
      pendingCode: readPendingReferral()?.code ?? null,
      enabled,
      isAuthenticated,
      hasCompletedOnboarding: user?.hasCompletedOnboarding === true,
      currentPath: location.pathname,
    });

    if (target) {
      firedRef.current = true;
      navigate(target, { replace: true });
    }
  }, [enabled, isAuthenticated, user, isLoading, location.pathname, navigate]);

  return null;
}
